import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * FAQ — the questions a recruitment leader actually asks before signing.
 *
 * Tone: clear, confident, customer-facing. Answers describe what the
 * customer GETS — not the technology under the hood. Where we mention
 * compliance or security, we keep it to the standards a procurement
 * team recognizes (SOC 2, DPDP) without diving into implementation.
 *
 * SEO: injects schema.org FAQPage JSON-LD into <head> so the questions
 * are eligible for Google rich results. Script is cleaned up on
 * unmount so it never leaks onto other routes.
 */
export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [
    {
      q: 'How accurate is the AI resume screening?',
      a: 'Our AI screening agent exhibit 98% accuracy in screening the resume.After screening every candidate score comes with a clear, detailed breakdown that help teams to choose the right fit for further processing.',
    },
    {
      q: 'Can the AI video interview really replace a recruiter?',
      a: 'It replaces the repetitive screening interviews that runs a role specific assessment based upon the job description like experience, communication, and role-specific competencies. Our AI agent conducts those interviews as real-time video calls, end-to-end, available 24/7. Your team gets the full video recording and transcript, and only spends time with the candidates worth meeting.',
    },
    {
      q: 'Which programming languages do you support for coding tests?',
      a: ' Python, Java, JavaScript, C, C++, C#, Ruby, Go, Rust, and many more all auto-graded with hidden test cases. Candidates can select language based upon the competency at the time of test.We can customise as per the need and the plans opted.',
    },
    {
      q: 'Will SearchJob.ai work alongside our existing ATS or HRMS?',
      a: 'Yes. You can export candidate data, scores, and assessment reports to CSV or Excel and import them into your existing systems today. Native pre-built connectors for major ATS and HRMS platforms are on our 2026 roadmap. Enterprise customers can request custom integrations through our team.',
    },
    {
      q: 'Is candidate data safe?',
      a: 'Yes. the data pertaining to candidate and screening is encrypted and secure on the servres. Access is controlled through role-based permissions, so can define who can see. We are actively preparing for formal SOC 2 and DPDP audits.',
    },
    // {
    //   q: 'Is there a free trial?',
    //   a: 'Yes. Start with the Starter plan free — 20 resume evaluations and 1 coding test per month, forever. No credit card required. Upgrade to Growth or Enterprise when you outgrow it. You can also book a personalized demo to see the platform end-to-end before you sign up.',
    // },
    // {
    //   q: 'How long does it take to get my team up and running?',
    //   a: 'Starter and Growth plans: under 30 minutes from sign-up to your first candidate assessment. Enterprise plans with SSO, custom integrations, and white-labeling: 1-2 weeks with a dedicated success manager who handles the rollout for your team.',
    // },
  ];

  // ─── schema.org FAQPage JSON-LD (Google rich-result eligible) ───
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
    // faqs is a static literal — run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className="relative py-14 lg:py-20 bg-background overflow-hidden"
      id="faq"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <HelpCircle className="w-3.5 h-3.5" />
            Frequently asked
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Questions hiring leaders
            <br />
            <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              ask before signing up.
            </span>
          </h2>
          <p className="text-base text-muted-foreground">
            Straight answers, written by our team — no marketing fluff.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <FAQItem
              key={faq.q}
              q={faq.q}
              a={faq.a}
              open={openIdx === idx}
              onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}
            />
          ))}
        </div>

        <div className="mt-10 text-center p-6 rounded-2xl bg-muted/40 border border-border">
          <p className="text-sm text-muted-foreground mb-2">
            Don&apos;t see your question here?
          </p>
          <a
            href="mailto:contact@searchjob.ai"
            className="text-sm font-semibold text-primary hover:underline"
          >
            Talk to our team at contact@searchjob.ai
          </a>
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a, open, onClick }) {
  return (
    <div
      className={`bg-card border rounded-xl overflow-hidden transition-colors ${
        open ? 'border-primary/40 shadow-md' : 'border-border'
      }`}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="text-base font-semibold text-foreground flex-1">
          {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-300 ${
            open ? 'rotate-180 text-primary' : ''
          }`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}
